import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { App } from "./App";
import { ApiError } from "./api/client";
import { useSession } from "./api/session";
import { StaleVersionBanner } from "./components/stale-version-banner";
import { IconProvider } from "./components/ui/icon";
import { Toaster } from "./components/ui/toast";
import "./styles/index.css";

/**
 * One place decides what an auth failure means, so no individual screen has to.
 * A 401 ends the session; a 403 carrying a gate code moves the session to that
 * gate instead of logging the user out (they are authenticated, just not done).
 */
function handleAuthFailure(error: unknown) {
  if (!(error instanceof ApiError)) return;
  const session = useSession.getState();
  if (error.isUnauthenticated) {
    session.logout();
    return;
  }
  if (error.code === "MUST_RESET_PASSWORD") session.setStage("must-reset");
  else if (error.code === "MUST_COMPLETE_ONBOARDING") session.setStage("onboarding");
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleAuthFailure }),
  mutationCache: new MutationCache({ onError: handleAuthFailure }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Auth and permission failures are decisions, not blips: retrying them
        // just delays the redirect the user actually needs.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) return false;
        return failureCount < 2;
      },
    },
  },
});

const container = document.getElementById("root");
if (!container) throw new Error("Root container missing from index.html");

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <IconProvider>
        <BrowserRouter>
          <App />
          <Toaster />
          <StaleVersionBanner />
        </BrowserRouter>
      </IconProvider>
    </QueryClientProvider>
  </StrictMode>,
);
